import {
  REHABILITATION_PAYMENT_OPTION,
  SEARCH_TYPES,
  SOLUTION_HOME_CARE,
  SOLUTION_MEDICAL_SUPPLIES,
} from "core/consts";
import {
  isCareSearch,
  isRehabSearch,
  matchesWhitelistScopes,
} from "core/model/auctions";
import {
  showMultipleSpecializations,
  showSpecializations,
} from "core/model/patients";
import {
  convertBirthDateIn,
  convertBirthDateOut,
  isPastDate,
} from "core/model/utils/dates";
import {
  Auction,
  BarthelAssessment,
  Careseeker,
  GetOntologiesType,
  GetOntologyType,
  SearchType,
} from "core/types";
import { fromUnixTime, getUnixTime, isSameDay, isValid } from "date-fns";
import {
  convertOutPayers,
  validatePayersInsurance,
} from "dsl/ecosystems/PatientAssessment/sharedModelDefinitions";
import { Iterable, List, fromJS } from "immutable";
import {
  ConversionModel,
  algoliaReactSelectDef,
  convertModelDefinition,
  encryptedDef,
  phoneNumberModel,
  toJS,
  valueDef,
} from "react-forms-state";
import {
  validateBarthelAssessmentDate,
  validateBarthelAssessmentScore,
  validateEarlyRehabAssessmentDate,
  validateEarlyRehabAssessmentScore,
  validateSingerAssessmentDate,
  validateSingerAssessmentScore,
} from "../../organisms/IndependenceScale/validators";

export function defaultSolutionsForSearchType(searchType: SearchType) {
  if (searchType == SEARCH_TYPES.MEDICAL_SUPPLIES)
    return [SOLUTION_MEDICAL_SUPPLIES];
  if (searchType == SEARCH_TYPES.HOME_CARE) return [SOLUTION_HOME_CARE];
  return null;
}

export function defaultSearchType(
  careseeker: Careseeker | undefined,
): SearchType | null {
  if (careseeker?.patient_types?.length == 1)
    return careseeker?.patient_types[0];

  return null;
}

const model: ConversionModel = {
  ...valueDef("id"),
  profile: {
    ...valueDef("search_location"),
  },
  ...valueDef("start_date_flexible", {
    show: matchesWhitelistScopes([
      {
        country: "DE",
        search_type: SEARCH_TYPES.CARE,
      },
      {
        country: "DE",
        search_type: SEARCH_TYPES.HOSPITAL,
      },
    ]),
  }),
  ...valueDef("start_date", {
    defaultValue: () => {
      return getUnixTime(new Date());
    },
    validate: (value, props) => {
      const { formInputValue, translations } = props;
      // date can't be empty
      if (!value) {
        return {
          customMessage:
            translations.assessments.formValidation.startDateMissing,
        };
      }

      // on initial creation of the patient, formInput is undefined, users can't enter date in past
      // on edit, formInput is filled, users can go to next step with same date, on change it can't be in the past either
      if (
        (!formInputValue && isPastDate(value, -1)) ||
        (formInputValue &&
          isPastDate(value, -1) &&
          !isSameDay(
            fromUnixTime(formInputValue.start_date),
            fromUnixTime(value),
          )) ||
        !isValid(value)
      ) {
        return {
          customMessage: translations.general.errorDateInPast,
        };
      }

      return true;
    },
  }),
  ...valueDef("search_type", {
    defaultValue: (props: AnyObject) => defaultSearchType(props.careseeker),
    validate(value, { translations }) {
      return value
        ? true
        : {
            customMessage:
              translations.assessments.formValidation.singleSelectError,
          };
    },
  }),
  ...valueDef("assessment_variant"),
  ...valueDef("solutions", {
    convertIn: (value: List<number> | undefined) => {
      if (!value) return null;
      return value;
    },
    convertOut: (value: List<number> | number) => {
      // Transport searches are limited to a single solution.
      // We therefore use a radio selector for the selection
      // which outputs a single solution.
      const solutions = toJS<number[] | number>(value);

      return Array.isArray(solutions) ? solutions : [solutions];
    },
    defaultValue: (props: AnyObject) => {
      const searchType = defaultSearchType(props.careseeker);
      if (searchType) return defaultSolutionsForSearchType(searchType);
      return undefined;
    },
    errorString: (t) => t.actions.missing,
    fieldRequired: true,
    validate: (value: List<number> | number | undefined, { translations }) => {
      // Also needed to handle the above mentioned transport case
      const solutions = toJS<number[] | number | undefined>(value);

      const isValid = Boolean(
        Array.isArray(solutions) ? solutions?.length : solutions,
      );

      return isValid
        ? true
        : {
            customMessage:
              translations.assessments.formValidation.typeOfCareMissing,
          };
    },
  }),
  ...valueDef("specializations", {
    convertIn: (
      value: any,
      props: {
        formInputValue: AnyObject | null;
        getOntologies: GetOntologiesType;
        getOntology: GetOntologyType;
      },
    ) => {
      const { formInputValue, getOntologies } = props;
      const inputValue = Iterable.isIterable(value) ? value.toJS() : value;
      const showMultiple = showMultipleSpecializations({
        searchType: formInputValue?.search_type,
      });

      if (
        !showSpecializations({ searchType: formInputValue?.search_type }) ||
        !inputValue?.length
      ) {
        return null;
      }
      const ontologyValues = getOntologies({
        sort: true,
        type: "specializations",
      });

      const specializations = ontologyValues
        .filter((ontVal) => inputValue.includes(ontVal.value))
        .map(({ name: label, value }) => ({ label, value }));

      return showMultiple ? specializations : specializations[0];
    },
    convertOut: (value: any) => {
      const outputValue = Iterable.isIterable(value) ? value.toJS() : value;
      if (outputValue) {
        const collection = Array.isArray(outputValue)
          ? outputValue
          : outputValue.value;
        if (Array.isArray(collection)) {
          return collection.map((selected) => selected.value);
        }
        return [collection];
      }
      return null;
    },
  }),
  ...valueDef("patient.id"),
  ...valueDef("patient.careseeker.id", {
    convertOut: (_, props) => {
      return props?.careseeker?.id;
    },
  }),
  ...valueDef("patient.profile.discharge_date", {
    convertOut(value, props, formOutputValue) {
      const formOutput = toJS<{ search_type: SearchType }>(formOutputValue);
      if (!isRehabSearch(formOutput.search_type)) {
        return null;
      }
      return value;
    },
    defaultValue: () => {
      return getUnixTime(new Date());
    },
    out_name: "discharge_date",
  }),
  patient: {
    ...valueDef("version"),
    ...valueDef("kis_import", { defaultValue: false }),
    ...valueDef("_kis_helper_element", {
      convertOut: () => undefined,
      defaultValue: () => null,
    }),
    ...valueDef("external_id"),
    ...valueDef("station_full_id", {
      convertIn: (input, _, immutableInput) => {
        const oldStation = immutableInput?.get("station");
        return input || { label: oldStation };
      },
      convertOut: (output) => {
        if (Number.isInteger(output)) return output;
        const outputValue = Iterable.isIterable(output)
          ? output.toJS()
          : output;
        return outputValue?.id ?? null;
      },
    }),
    // Primarily KIS fields
    diagnosis: {
      ...valueDef("main_diagnosis"),
      ...valueDef("secondary_diagnosis"),
      ...valueDef("hl7_main_diagnoses"),
      ...valueDef("hl7_secondary_diagnoses"),
      ...valueDef("allergies"),
      ...valueDef("medical_history"),
      dependency_diagnosis: {
        ...valueDef("barthel_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },
          validate(valueMap, props) {
            const value = toJS<BarthelAssessment | null>(valueMap);

            const scoreValidation = validateBarthelAssessmentScore(
              value?.score,
              props,
              { barthel_assessment_date: value?.date },
            );
            if (scoreValidation !== true) {
              return scoreValidation.customMessage;
            }

            const dateValidation = validateBarthelAssessmentDate(
              value?.date,
              props,
              { singer_assessment_score: value?.score },
            );
            if (dateValidation !== true) {
              return dateValidation.customMessage;
            }

            return true;
          },
        }),
        ...valueDef("early_rehab_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },

          validate(valueMap, props) {
            const value = toJS<BarthelAssessment | null>(valueMap);

            const scoreValidation = validateEarlyRehabAssessmentScore(
              value?.score,
              props,
              { early_rehab_assessment_date: value?.date },
            );
            if (scoreValidation !== true) {
              return scoreValidation.customMessage;
            }

            const dateValidation = validateEarlyRehabAssessmentDate(
              value?.date,
              props,
              { singer_assessment_score: value?.score },
            );
            if (dateValidation !== true) {
              return dateValidation.customMessage;
            }

            return true;
          },
        }),
        ...valueDef("singer_assessment", {
          convertOut(valueMap) {
            const value = toJS<BarthelAssessment | null>(valueMap);
            if (!value) return null;

            return {
              ...value,
              form:
                value.form &&
                Object.values(value.form).some(
                  (value) => typeof value === "number",
                )
                  ? value.form
                  : null,
            };
          },
          validate(valueMap, props) {
            const value = toJS<BarthelAssessment | null>(valueMap);

            const scoreValidation = validateSingerAssessmentScore(
              value?.score,
              props,
              { singer_assessment_date: value?.date },
            );
            if (scoreValidation !== true) {
              return scoreValidation.customMessage;
            }

            const dateValidation = validateSingerAssessmentDate(
              value?.date,
              props,
              { singer_assessment_score: value?.score },
            );
            if (dateValidation !== true) {
              return dateValidation.customMessage;
            }

            return true;
          },
        }),
      },
      hospital_stay: {
        ...valueDef("operation_date"),
        ...valueDef("operation_description"),
        ...valueDef("doctor_in_charge_in_hospital"),
        ...valueDef("doctor_in_charge_in_hospital_phone", {
          ...phoneNumberModel(false),
          defaultValue: (props: any) => {
            const stationInformation = props.careseeker?.stations_full;
            return stationInformation?.length === 1
              ? stationInformation[0].phone_number
              : "";
          },
        }),
      },
      ...valueDef("infection_and_germs"),
      ...valueDef("infection_and_germs_state"),
    },
    profile: {
      financing: {
        ...encryptedDef("insurance_number"),
        ...valueDef("insurance_id"),
        ...valueDef("insurance"),
        carelevel: {
          out: "carelevel",
          ...valueDef("level"),
        },
      },
      ...valueDef("gender"),
      ...encryptedDef("first_name"),
      ...encryptedDef("last_name"),
      ...encryptedDef("height"),
      ...encryptedDef("weight"),
      ...encryptedDef("birth_date", {
        convertIn: convertBirthDateIn,
        convertOut: convertBirthDateOut,
      }),
      communication: {
        ...valueDef("has_guardian"),
        ...encryptedDef("guardian_contact_information"),
        ...valueDef("relatives_available"),
        ...encryptedDef("relatives_description"),
        ...valueDef("patient_is_contact"),
        ...encryptedDef("patient_is_contact_description"),
      },
      general_practitioner: {
        ...valueDef("name", {
          out_name: "general_practitioner_name",
        }),
        ...valueDef("contact_details", {
          out_name: "general_practitioner_contact_details",
        }),
      },
      // end KIS only fields

      ...valueDef("care_duration_in_days", {
        validate: (value, { translations }, globalProps) => {
          const { search_type } = toJS<Partial<Auction>>(globalProps);
          if (isCareSearch(search_type)) {
            return value
              ? true
              : {
                  customMessage:
                    translations.assessments.formValidation.careDurationMissing,
                };
          }
          return true;
        },
      }),
      ...valueDef("interested_long_term_stay"),
      ...valueDef("direct_transfer_necessary"),
    },
    social_worker: {
      complex: true,
      convertIn: (socialWorker, props) => {
        if (socialWorker == null) return props?.account?.id;
        return socialWorker.getIn(["id"], null);
      },
      convertOut: (output) => {
        const data = Iterable.isIterable(output) ? output.toJS() : output;
        if (!data) return {};
        return { id: data.value || data };
      },
      default: (props: any) =>
        props.account ? fromJS({ id: props.account.id }) : fromJS({}),
      out: "social_worker_account_id",
    },
  },
  payers: {
    ...valueDef("selected_payment_method", {
      convertOut: (value, _props, globalValue) => {
        const formValue = Iterable.isIterable(globalValue)
          ? globalValue.toJS()
          : globalValue;
        // if insurance is added to the patient creation then the selected payment option is decided there
        if (formValue?.payers_insurance?.id != null)
          return REHABILITATION_PAYMENT_OPTION.INSURANCE;
        return value;
      },
    }),
    ...algoliaReactSelectDef("insurance", {
      convertOut: convertOutPayers,
      out_name: "payers_insurance",
      validate: validatePayersInsurance,
    }),
  },
};

export default convertModelDefinition(model);
